import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ContentCopy, DataObject, KeyOutlined, PaymentOutlined, QueryStatsOutlined } from '@mui/icons-material';

import { Page } from '../../../components';
import { api } from '../../../http';
import { useGenericQuery } from '../../../services/react-query/generic.query';

const INK = '#0E1230';

// TODO: swap for the HubSpot request form Jonas is providing (routes to
// James/AJ) — the button then becomes a plain link. Until then the button
// copies the sales address; a mailto would launch whatever the OS considers
// the mail client, which for many users is an unconfigured Apple Mail.
const CONTACT_SALES_EMAIL = 'sales@pomsafe.com';

// Hidden for the Sept 9 conference push: no payment prompts or per-search
// pricing on this screen. The billing endpoints and flow stay intact behind
// this flag — flip to true when pricing is signed off.
const SHOW_BILLING_UI = false;

// Also walked back for the conference: self-serve key generation. Every org
// sees the contact-POM treatment (greyed generate + contact button) until
// billing is live; keygen and billing flip back on together.
const SELF_SERVE_KEYS_ENABLED = false;

interface IraApiStatus {
    apiAccess: 'salesGated' | 'selfService';
    billingConfigured: boolean;
    cardOnFile: boolean;
    hasApiKey: boolean;
    apiKeyMasked: string | null;
    usageThisMonth: number;
    pricePerSearchCents: number | null;
    apiBaseUrl: string | null;
}

const SectionCard: React.FC<{ icon: React.ReactNode; title: string; subtitle: string; children: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
    <Card sx={{ borderRadius: '20px', border: '1px solid #E6EBF2', boxShadow: '0 12px 32px -20px rgba(14,18,48,0.16)' }}>
        <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: '#206FA5', boxShadow: '0 6px 16px -6px rgba(32,111,165,0.6)' }}>
                    {icon}
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight={600}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                </Box>
            </Box>
            {children}
        </CardContent>
    </Card>
);

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
    <Box
        component="pre"
        sx={{
            m: 0,
            p: 2.5,
            borderRadius: '12px',
            backgroundColor: INK,
            color: '#D7E4F5',
            fontSize: 13,
            lineHeight: 1.65,
            overflowX: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
    >
        {children}
    </Box>
);

export const ApiAccessScreen: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: status, refetch, isLoading } = useGenericQuery<IraApiStatus>('ira-api-status', 'ira-api/status', undefined, undefined, true, 0);
    const [freshKey, setFreshKey] = useState<string | null>(null);
    const [busy, setBusy] = useState<'billing' | 'key' | null>(null);

    // Returning from the Stripe Checkout setup redirect.
    useEffect(() => {
        const setup = searchParams.get('setup');
        const sessionId = searchParams.get('session_id');
        if (!setup) return;
        setSearchParams({}, { replace: true });
        if (setup === 'cancelled') {
            toast('Payment setup was cancelled', { type: 'info', autoClose: 2500 });
            return;
        }
        if (setup === 'success' && sessionId) {
            api.post('/ira-api/billing/confirm', { sessionId })
                .then(() => { toast('Payment method saved', { type: 'success', autoClose: 2500 }); refetch(); })
                .catch(() => toast('Could not confirm payment setup — try again', { type: 'error' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startBillingSetup = async () => {
        setBusy('billing');
        try {
            const { data } = await api.post('/ira-api/billing/setup-session', {});
            if (data?.result?.configured === false) {
                toast('Billing is not configured in this environment yet', { type: 'info' });
            } else if (data?.result?.url) {
                window.location.assign(data.result.url);
                return;
            }
        } catch {
            toast('Could not start payment setup', { type: 'error' });
        }
        setBusy(null);
    };

    const generateKey = async () => {
        setBusy('key');
        try {
            const { data } = await api.post('/ira-api/generate-key', {});
            if (data?.result?.configured === false) {
                toast('Key generation is not configured in this environment yet', { type: 'info' });
            } else if (data?.result?.apiKey) {
                setFreshKey(data.result.apiKey);
                refetch();
            }
        } catch (error: any) {
            const message = error?.response?.data?.message ?? '';
            toast(
                message.includes('BILLING_CARD_REQUIRED')
                    ? 'Add a payment method before generating a key'
                    : 'Could not generate an API key',
                { type: 'error' },
            );
        }
        setBusy(null);
    };

    const price = status?.pricePerSearchCents;
    const priceLabel = price ? `$${(price / 100).toFixed(2)} per search` : null;
    const baseUrl = status?.apiBaseUrl || 'https://protect.pom-bright.com';
    const selfService = status?.apiAccess === 'selfService';

    return (
        <Page title="API Access">
            <Container maxWidth={false}>
                <Box sx={{ maxWidth: 1240, mx: 'auto', pt: { xs: 1, md: 2 }, pb: 6 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#206FA5', mb: 1 }}>
                        Developers
                    </Typography>
                    <Typography component="h1" sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700, letterSpacing: '-0.02em', color: INK, mb: 1 }}>
                        API Access
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: 'text.secondary', maxWidth: 680, mb: 4 }}>
                        Run Intake Risk Assessments from your own intake system. Every search is recorded
                        and returns the same report your team sees here.
                    </Typography>

                    <Stack spacing={3}>
                        <SectionCard icon={<KeyOutlined sx={{ color: '#fff' }} />} title="API Key" subtitle="Authenticates your systems against the IRA API">
                            {(!selfService || !SELF_SERVE_KEYS_ENABLED) ? (
                                <Stack spacing={2} alignItems="flex-start">
                                    <Alert severity="info" sx={{ borderRadius: '10px', width: '100%' }}>
                                        API access is enabled by POM Safe. Reach out at{' '}
                                        <strong>{CONTACT_SALES_EMAIL}</strong> and we will get your
                                        organization set up.
                                    </Alert>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="contained"
                                            startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                                            onClick={async () => {
                                                try {
                                                    await navigator.clipboard.writeText(CONTACT_SALES_EMAIL);
                                                    toast(`Email address copied — ${CONTACT_SALES_EMAIL}`, { type: 'success', autoClose: 2000 });
                                                } catch {
                                                    toast(`Could not copy — email us at ${CONTACT_SALES_EMAIL}`, { type: 'warning' });
                                                }
                                            }}
                                        >
                                            Contact POM Safe
                                        </Button>
                                        <Tooltip title="Contact POM Safe to enable API access for your organization">
                                            <span><Button variant="outlined" disabled>Generate API key</Button></span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Stack spacing={2} alignItems="flex-start">
                                    {SHOW_BILLING_UI && status?.billingConfigured === false && (
                                        <Alert severity="info" sx={{ borderRadius: '10px', width: '100%' }}>
                                            Billing is not configured in this environment; keys can be generated without a card for testing.
                                        </Alert>
                                    )}
                                    {SHOW_BILLING_UI && status?.billingConfigured && (
                                        status?.cardOnFile ? (
                                            <Chip icon={<PaymentOutlined />} label="Card on file" color="success" variant="outlined" />
                                        ) : (
                                            <Alert severity="warning" sx={{ borderRadius: '10px', width: '100%' }}>
                                                Add a payment method to generate your API key.
                                                {priceLabel ? ` Searches are billed at ${priceLabel}.` : ''}
                                            </Alert>
                                        )
                                    )}
                                    {freshKey ? (
                                        <Alert severity="success" sx={{ borderRadius: '10px', width: '100%', '& .MuiAlert-message': { width: '100%' } }}>
                                            <Typography fontWeight={600} sx={{ mb: 1 }}>
                                                Your API key — copy it now, it is only shown once:
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                                                {freshKey}
                                                <IconButton
                                                    size="small"
                                                    onClick={async () => {
                                                        // The key is shown once — a silent copy failure here loses it.
                                                        try {
                                                            await navigator.clipboard.writeText(freshKey);
                                                            toast('Key copied', { autoClose: 1200, type: 'success' });
                                                        } catch {
                                                            toast('Could not copy — select the key text and copy it manually', { type: 'warning' });
                                                        }
                                                    }}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Alert>
                                    ) : status?.hasApiKey && (
                                        <Typography sx={{ fontFamily: 'monospace', fontSize: 14, color: 'text.secondary' }}>
                                            Active key: {status.apiKeyMasked}
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={2}>
                                        {SHOW_BILLING_UI && status?.billingConfigured && !status?.cardOnFile && (
                                            <LoadingButton variant="contained" loading={busy === 'billing'} onClick={startBillingSetup} startIcon={<PaymentOutlined />}>
                                                Add payment method
                                            </LoadingButton>
                                        )}
                                        <LoadingButton
                                            variant={SHOW_BILLING_UI && status?.billingConfigured && !status?.cardOnFile ? 'outlined' : 'contained'}
                                            loading={busy === 'key'}
                                            disabled={isLoading || (SHOW_BILLING_UI && status?.billingConfigured && !status?.cardOnFile)}
                                            onClick={generateKey}
                                        >
                                            {status?.hasApiKey ? 'Generate new key' : 'Generate API key'}
                                        </LoadingButton>
                                    </Stack>
                                </Stack>
                            )}
                        </SectionCard>

                        <SectionCard icon={<QueryStatsOutlined sx={{ color: '#fff' }} />} title="Usage" subtitle="Searches run by your organization this month">
                            <Stack direction="row" spacing={4} alignItems="baseline">
                                <Typography sx={{ fontSize: 40, fontWeight: 700, color: INK }}>{status?.usageThisMonth ?? '—'}</Typography>
                                <Typography color="text.secondary">searches this month</Typography>
                                {SHOW_BILLING_UI && priceLabel && status?.usageThisMonth != null && (
                                    <Typography color="text.secondary">
                                        {priceLabel} · est. ${((status.usageThisMonth * (price ?? 0)) / 100).toFixed(2)}
                                    </Typography>
                                )}
                            </Stack>
                        </SectionCard>

                        <SectionCard icon={<DataObject sx={{ color: '#fff' }} />} title="Documentation" subtitle="Everything you need for a first request">
                            <Stack spacing={2.5}>
                                <Typography variant="body2" color="text.secondary">
                                    Authenticate with your API key in the <code>x-api-token</code> header and your
                                    subscription id in the <code>subscriptionid</code> header. The JSON endpoint returns
                                    the full assessment; the PDF variants return the branded report document.
                                </Typography>
                                <CodeBlock>{`POST ${baseUrl}/external/intakeRiskAssessment
POST ${baseUrl}/external/intakeRiskAssessmentPdf
POST ${baseUrl}/external/intakeRiskAssessmentPdfBase64`}</CodeBlock>
                                <CodeBlock>{`curl -X POST '${baseUrl}/external/intakeRiskAssessment' \\
  -H 'Content-Type: application/json' \\
  -H 'x-api-token: YOUR_API_KEY' \\
  -H 'subscriptionid: YOUR_SUBSCRIPTION_ID' \\
  -d '{
    "firstName": "John",
    "lastName": "Doe",
    "dateOfBirth": "1980-05-15",
    "address": "123 Main St, Hartford, CT 06103"
  }'`}</CodeBlock>
                                <Typography variant="body2" color="text.secondary">
                                    The response includes the registry match result, offenders at the address,
                                    criminal and court records, area safety ratings, and related news, the same
                                    sections as the report in this app. Results are cached for 60 minutes per
                                    subject, and every search is recorded for your audit trail.
                                </Typography>
                            </Stack>
                        </SectionCard>
                    </Stack>
                </Box>
            </Container>
        </Page>
    );
};
